import React from 'react';
import { Trans } from 'react-i18next';
import { View } from 'react-native';

import { AppUpdate } from '@components/AppUpdate';
import { Sidebar } from '@components/Sidebar';
import { Typography } from '@components/Typography';
import { makeStyles } from '@providers/Theme';
import { useIsMobile } from '@hooks';
import { useLookupsStore } from '@stores/lookups';

import { MobileAppBar } from './MobileAppBar';

type MainScreenLayoutProps = { children: React.ReactNode };

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    height: '100%',
    flexDirection: 'column',
    on: {
      md: {
        flexDirection: 'row',
      },
    },
  },
  desktopSidebar: {
    on: {
      xl: {
        flex: 1,
      },
      md: {
        flex: 2,
      },
    },
  },
  burgerMenuButton: {
    display: undefined,
    on: {
      md: {
        display: 'none',
      },
    },
  },
  content: {
    flexDirection: 'row',
    flex: 5,
    justifyContent: 'flex-start',
    overflowX: 'auto',
    overflowY: 'auto',
    backgroundColor: 'white',
    height: theme.screen.height - 64, // 64 -> AppBar height
    on: {
      md: {
        height: '100%',
      },
    },
  },
  loading: {
    width: '100%',
    height: '100%',
    backgroundColor: 'white',
  },
}));

export const MainScreenLayout: React.FC<MainScreenLayoutProps> = ({
  children,
}) => {
  const styles = useStyles();
  const isMobile = useIsMobile();
  const lookups = useLookupsStore();

  if (lookups.loading) {
    return (
      <View style={styles.loading}>
        <Typography>
          <Trans i18nKey="global.loading" defaults="Loading..." />
        </Typography>
      </View>
    );
  }

  return (
    <View style={styles.root}>
      {!isMobile && (
        <View style={styles.desktopSidebar}>
          <Sidebar />
        </View>
      )}

      {isMobile && <MobileAppBar />}
      <View style={styles.content}>
        {children}
        {isMobile && <AppUpdate />}
      </View>
    </View>
  );
};

// this is only for native router
export function withMainScreenLayout(
  Component: React.ComponentType<{}>,
): React.FC<{}> {
  return function ComponentWithMainScreenLayout() {
    return (
      <MainScreenLayout>
        <Component />
      </MainScreenLayout>
    );
  };
}
